import { postJson, getJson } from '@/lib/api';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: 'user' | 'admin' | 'superadmin';
}

export interface LoginResponse {
  ok: boolean;
  next: 'code' | 'enrol' | 'done';
  setPassword?: boolean;
}

export interface EnrolMaterial {
  secret: string;
  uri: string;
  issuer: string;
  account: string;
}

export const authApi = {
  me: () => getJson<SessionUser>('/api/auth/me'),
  login: (email: string, password: string) =>
    postJson<LoginResponse>('/api/auth/login', { email, password, flow: 'password' }),
  logout: () => postJson<{ ok: true }>('/api/auth/logout', {}),
  verify: (code: string, trust: boolean) =>
    postJson<{ ok: true; setPassword?: boolean }>('/api/auth/mfa/verify', { code, trust }),
  recover: (code: string) =>
    postJson<{ ok: true; next: 'enrol'; setPassword?: boolean } & EnrolMaterial>('/api/auth/mfa/recover', { code }),
  enrol: () => postJson<{ ok: true } & EnrolMaterial>('/api/auth/mfa/enrol', {}),
  confirm: (code: string, trust: boolean) =>
    postJson<{ ok: true; recoveryCodes: string[]; setPassword?: boolean }>('/api/auth/mfa/confirm', { code, trust }),
  changePassword: (currentPassword: string, newPassword: string) =>
    postJson<{ ok: true }>('/api/auth/password', { currentPassword, newPassword }),
};

/** Control characters and backslash: what a URL parser would strip or
 *  rewrite into another origin. Same rule as the server's safeReturnTo. */
function hasUnsafeChars(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 32 || c === 127 || c === 92) return true;
  }
  return false;
}

/** Only a same-origin relative path is honoured; anything else becomes "/". */
export function safeReturnTo(value: string | null): string {
  const returnTo = value || '/';
  if (returnTo.charAt(0) !== '/' || hasUnsafeChars(returnTo)) return '/';
  try {
    const u = new URL(returnTo, window.location.origin);
    if (u.origin !== window.location.origin || u.pathname.startsWith('//')) return '/';
    return u.pathname + u.search + u.hash;
  } catch {
    return '/';
  }
}
